import { Component, inject, output } from '@angular/core';
import { EMPTY, throwError } from 'rxjs';
import { SignaturePadComponent } from './signature-pad.component';
import { SignupCardComponent } from '../ui/signup-card.component';
import { ContactFieldsComponent } from '../contact/contact-fields.component';
import { ContactConsentsComponent } from '../contact/contact-consents.component';
import { FormCtaComponent } from '../ui/form-cta.component';
import { ContactFormBase } from '../contact/contact-form-base';
import { validateContact } from '../contact/contact-form.store';
import { SignupEnrollmentService } from './signup-enrollment.service';

@Component({
  selector: 'app-enroll-form',
  standalone: true,
  imports: [SignaturePadComponent, SignupCardComponent, ContactFieldsComponent, ContactConsentsComponent, FormCtaComponent],
  template: `
    <app-signup-card>
      <div class="overline">{{ stepLabel }}</div>
      <h1>Enroll to start protesting.</h1>
      <p class="lede">Contact info and one signed authorization (Form 50-162) so our licensed Texas team can represent you.</p>

      <form (submit)="onSubmit($event)" novalidate>
        <!-- Locked once signed: Form 50-162 was signed with these details -->
        <!-- Changes update Form 50-162 once it's generated (debounced) -->
        <app-contact-fields [(value)]="contact" [(errors)]="errors" [phoneRequired]="phoneRequired" (input)="onChange()"
          [class.locked]="enrollment.locksContact()" [attr.inert]="enrollment.locksContact() ? '' : null" />
        <p class="disclaimer">View <a target="_blank" [href]="serviceTermsUrl">Property Tax Consulting Terms of Service</a></p>

        <!-- Sign form checks the required fields; once they're all present, Form 50-162 is generated -->
        <app-signature-pad (signRequested)="onSign()" />

        <app-contact-consents [(value)]="contact" [(errors)]="errors"
          [class.locked]="enrollment.locksContact()" [attr.inert]="enrollment.locksContact() ? '' : null" />

        <app-form-cta [label]="ctaLabel" [submittingLabel]="ctaSubmittingLabel"
          [reassurance]="reassurance" [submitting]="submitting()" [error]="submitError()"
          [disabled]="!enrollment.isSigned()" />
      </form>
    </app-signup-card>
  `,
  styles: `
    /* Yellow-1000 on white for AA-safe eyebrow text (Yellow-800 fails at this size) */
    .overline {
      font-size: 14px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase;
      color: var(--kin-yellow-1000); margin-bottom: 8px;
    }
    h1 { font-size: 28px; font-weight: 600; line-height: 1.2; letter-spacing: -0.5px; margin: 0 0 8px; }
    .lede { font-size: 16px; line-height: 1.6; color: var(--kin-text-secondary); margin: 0 0 24px; }
    /* Same type as the TCPA and marketing consents; the link takes the page's underlined link style */
    .disclaimer { font-size: 12px; line-height: 1.5; color: var(--kin-text-secondary); margin: 0; }
    /* Same dimming as the page's frozen state, without the blur, so the saved details stay readable */
    app-contact-fields, app-contact-consents { transition: opacity 120ms var(--kin-ease-out); }
    .locked { opacity: 0.55; pointer-events: none; }
    @media (prefers-reduced-motion: reduce) {
      app-contact-fields, app-contact-consents { transition: none; }
    }
  `,
})
export class EnrollFormComponent extends ContactFormBase {
  protected readonly enrollment = inject(SignupEnrollmentService);

  protected readonly phoneRequired = true;

  stepLabel = 'Step 2 of 2';

  /** Sign form was pressed before "Is this you?" was answered; the page shows the error on the property card. */
  readonly ownerRelRequired = output<void>();

  ctaLabel = 'Enroll for free';
  ctaSubmittingLabel = 'Enrolling…';
  reassurance = 'Secure · No credit card';
  serviceTermsUrl = 'https://www.kin.com/property-tax-consulting-service-terms/';

  /**
   * The text inputs and (from the page) the "Is this you?" answer: once Form 50-162 is generated, it's updated
   * from them, debounced so more changes can be collected.
   */
  onChange(): void {
    this.enrollment.fieldsChanged(this.submission());
  }

  /** Sign form: shows what's missing, the same way submit does, or generates Form 50-162. */
  protected onSign(): void {
    const errors = validateContact(this.contact(), { phoneRequired: this.phoneRequired });
    this.errors.set(errors);
    const unanswered = this.addressStore.ownerRelationship() === null;
    if (unanswered) this.ownerRelRequired.emit();
    if (Object.keys(errors).length || unanswered) return;
    this.enrollment.requestSigning(this.submission());
  }

  /** The intake is saved by Sign form; enrolling signs off on it with the one submit. */
  protected send() {
    // Belt and braces: the page only renders this form for a validated address in a county sign-up enrolls
    if (!this.addressStore.isServiceable()) {
      return throwError(() => new Error('Enrollment requires a validated address in a county we serve'));
    }
    if (this.enrollment.isSigned()) return this.enrollment.submitIntake();
    // Not signed yet: point at the pad
    this.enrollment.requireSignature();
    return EMPTY;
  }
}
